import { useEffect, useMemo, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";
import { MdClose, MdKeyboardArrowDown, MdKeyboardArrowUp, MdCheck } from "react-icons/md";

/**
 * Data model expected from the parent:
 * roles = [
 *   {
 *     id: string | number,
 *     name: string,
 *     users: [{ id: string | number, name: string }]
 *   }
 * ]
 *
 * selectedUserIds = array of user ids (string | number)
 */
const RoleUserSelectionModal = ({ shown, roles = [], selectedUserIds = [], onCancel, onSave }) => {
  const [checkedUsers, setCheckedUsers] = useState({});
  const [expandedRoles, setExpandedRoles] = useState({});

  // initialize check states whenever modal opens or data changes
  useEffect(() => {
    if (!shown) return;

    const initialChecks = {};
    roles.forEach((role) => {
      role.users?.forEach((user) => {
        initialChecks[user.id] = selectedUserIds.includes(String(user.id)) || selectedUserIds.includes(user.id);
      });
    });
    setCheckedUsers(initialChecks);

    const initialExpanded = {};
    roles.forEach((role) => {
      initialExpanded[role.id] = true;
    });
    setExpandedRoles(initialExpanded);
  }, [shown, roles, selectedUserIds]);

  const toggleRoleExpand = (roleId) => {
    setExpandedRoles((prev) => ({ ...prev, [roleId]: !prev[roleId] }));
  };

  const setRoleChecked = (role, isChecked) => {
    const next = { ...checkedUsers };
    role.users?.forEach((user) => {
      next[user.id] = isChecked;
    });
    setCheckedUsers(next);
  };

  const setUserChecked = (userId, isChecked) => {
    setCheckedUsers((prev) => ({ ...prev, [userId]: isChecked }));
  };

  const roleState = useMemo(() => {
    const state = {};
    roles.forEach((role) => {
      const total = role.users?.length || 0;
      const selectedCount = role.users?.reduce((acc, user) => acc + (checkedUsers[user.id] ? 1 : 0), 0) || 0;
      state[role.id] = {
        checked: total > 0 && selectedCount === total,
        indeterminate: selectedCount > 0 && selectedCount < total,
      };
    });
    return state;
  }, [roles, checkedUsers]);

  const handleSave = () => {
    const selectedUsers = [];
    roles.forEach((role) => {
      role.users?.forEach((user) => {
        if (checkedUsers[user.id]) {
          selectedUsers.push(user);
        }
      });
    });
    onSave?.(selectedUsers);
  };

  if (!shown) return null;

  return (
    <div className="fixed w-screen h-screen left-0 top-0 bg-black bg-opacity-30 flex items-center justify-center z-30">
      <OutsideClickHandler onOutsideClick={() => onCancel?.()}>
        <div data-tour="rep-pick-techs" className="w-[440px] max-h-[90vh] flex flex-col bg-white rounded-[12px] shadow-[0px_10px_10px_-5px_rgba(0,0,0,0.04),0px_20px_25px_-5px_rgba(0,0,0,0.10)]">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 px-6 py-4 border-b-[1px] border-borderColor">
            <div className="flex flex-col gap-1">
              <p className="font-OpenSansSemiBold text-[20px] leading-[28px] capitalize text-titleColor">
                Select Technicians
              </p>
              <p className="font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-subTitleColor">
                Choose technicians by role.
              </p>
            </div>
            <button
              type="button"
              onClick={() => onCancel?.()}
              className="w-8 h-8 rounded-full flex items-center justify-center text-[#868DA6] hover:text-titleColor"
            >
              <MdClose size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-auto px-6 py-4">
            {roles.map((role) => {
              const currentRoleState = roleState[role.id] || { checked: false, indeterminate: false };
              return (
                <div key={role.id} className="mb-3">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => toggleRoleExpand(role.id)}
                      className="text-subTitleColor hover:text-titleColor"
                    >
                      {expandedRoles[role.id] ? <MdKeyboardArrowUp size={20} /> : <MdKeyboardArrowDown size={20} />}
                    </button>
                    <CheckTile
                      checked={currentRoleState.checked}
                      indeterminate={currentRoleState.indeterminate}
                      onToggle={() => setRoleChecked(role, !currentRoleState.checked)}
                    />
                    <p className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-titleColor">{`${role.name}s`}</p>
                  </div>

                  {expandedRoles[role.id] && (
                    <div className="pl-[42px] pt-2 flex flex-col gap-2">
                      {role.users?.map((user) => (
                        <div
                          key={user.id}
                          onClick={() => setUserChecked(user.id, !checkedUsers[user.id])}
                          className="flex items-center gap-3 cursor-pointer"
                        >
                          <CheckTile
                            checked={!!checkedUsers[user.id]}
                            onToggle={() => setUserChecked(user.id, !checkedUsers[user.id])}
                          />
                          <span className="font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-titleColor">
                            {user.name}
                          </span>
                        </div>
                      ))}
                      {!role.users?.length && (
                        <p className="font-OpenSansRegular text-[12px] leading-[18px] text-subTitleColor italic">
                          No users
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {!roles.length && (
              <p className="font-OpenSansRegular text-[14px] leading-[20px] text-subTitleColor">No roles available.</p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-4 px-6 py-4 border-t-[1px] border-borderColor">
            <button
              type="button"
              onClick={onCancel}
              className="h-10 px-1 rounded-[6px] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-subTitleColor"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="h-10 px-4 rounded-[8px] bg-blueColor flex items-center justify-center font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-white cursor-pointer"
            >
              Save
            </button>
          </div>
        </div>
      </OutsideClickHandler>
    </div>
  );
};

const CheckTile = ({ checked, indeterminate, onToggle }) => (
  <button
    type="button"
    onClick={(event) => {
      event.stopPropagation();
      onToggle();
    }}
    className={`w-[18px] h-[18px] rounded-[4px] border flex items-center justify-center shrink-0 transition-colors ${
      checked || indeterminate ? "border-[#0065FD] bg-[#0065FD]" : "border-[#C9CEE2] bg-white"
    }`}
  >
    {checked && <MdCheck size={14} className="text-white" />}
    {!checked && indeterminate && <span className="w-[8px] h-[2px] rounded-full bg-white" />}
  </button>
);

export default RoleUserSelectionModal;
